import dayjs from "dayjs"
import { useEffect, useMemo } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { atom, useRecoilState } from "recoil"
import {
  NudgeTakeFragment,
  OrganizationFragment,
  OrganizationMemberClientFragment,
  QuestionnaireTakeFragment,
  useGetOrganizationFeaturesByOrgIdQuery,
  useGetOrgPractitionerCountsQuery,
  useGetSupervisorsByOrgIdQuery,
  useMyOrganizationQuery,
  useUpdateOrganizationLocationMutation,
  useUpdateOrganizationNameMutation,
} from "../generated/graphql"
import {
  NudgeDomain,
  Pending,
  Query,
  QuestionnaireType,
} from "../utils/constants"
import { getQuestionnaireScore, sortQuestionnaireTake } from "../utils/helpers"
import {
  ClientProgressData,
  StaffOverviewData,
  StaffTableData,
  TechniqueProfileData,
} from "../utils/types"
import { useGraphQLClient } from "./useGraphQLClient"
import { useUser } from "./useUser"

export const isDepressionTake = (take: QuestionnaireTakeFragment) =>
  take.questionnaire_schedule.questionnaire.name ===
  QuestionnaireType.DEPRESSION

export const isAnxietyTake = (take: QuestionnaireTakeFragment) =>
  take.questionnaire_schedule.questionnaire.name === QuestionnaireType.ANXIETY

export const isSubstanceUseTake = (take: QuestionnaireTakeFragment) =>
  take.questionnaire_schedule.questionnaire.name ===
  QuestionnaireType.SUBSTANCE_USE

// Either get the high score for the set of takes or -1 if no completed takes found
export function getHighScore(
  takes: QuestionnaireTakeFragment[],
  sameOrAfterDate: dayjs.Dayjs,
  beforeDate: dayjs.Dayjs
) {
  const monthTakes = takes.filter(
    (take) =>
      take.completed_at &&
      dayjs(take.completed_at).isSameOrAfter(sameOrAfterDate) &&
      dayjs(take.completed_at).isBefore(beforeDate)
  )
  if (monthTakes.length === 0) {
    // ensures that the next month will be stable or improving
    return -1
  }
  const depressionScore = getQuestionnaireScore(
    monthTakes.filter(isDepressionTake).sort(sortQuestionnaireTake)
  )
  const anxietyScore = getQuestionnaireScore(
    monthTakes.filter(isAnxietyTake).sort(sortQuestionnaireTake)
  )
  const substanceUseScore = getQuestionnaireScore(
    monthTakes.filter(isSubstanceUseTake).sort(sortQuestionnaireTake)
  )
  const scores = [depressionScore, anxietyScore, substanceUseScore].sort(
    (a, b) => (a.score > b.score ? -1 : a.score < b.score ? 1 : 0)
  )
  return scores[0].score
}

export function iterateProgress(
  month: {
    improving: number
    stable: number
    declining: number
  },
  currentScore: number,
  previousScore: number
) {
  if (currentScore < previousScore) {
    month.declining++
  } else if (currentScore === previousScore) {
    month.stable++
  } else if (currentScore > previousScore) {
    month.improving++
  }
  return month
}

export function getClientCountBeforeMonth(
  clients: OrganizationMemberClientFragment[],
  beforeDate: dayjs.Dayjs
) {
  return clients.filter((client) =>
    dayjs(client.user.created_at).isBefore(beforeDate)
  ).length
}

export function getProgressPercent(count: number, total: number) {
  let result = count / total
  if (isNaN(result)) {
    result = 0
  }
  return result * 100
}

export function getMonthPercentages(
  month: {
    improving: number
    stable: number
    declining: number
  },
  clientTotal: number
) {
  month.improving = getProgressPercent(month.improving, clientTotal)
  month.declining = getProgressPercent(month.declining, clientTotal)
  month.stable = getProgressPercent(month.stable, clientTotal)
  if (month.improving === 0 && month.declining === 0) {
    month.stable = 100
  }
  return month
}

export function getTechniqueProfileData(organization: OrganizationFragment) {
  let allNudgeTakes: NudgeTakeFragment[] = []
  organization.organization_members?.forEach((member) => {
    member.practitioner.practitioner_invite_codes.forEach((code) => {
      const user = code.user
      if (!user) {
        return
      }
      const months = 6
      const sixMonthsAgo = dayjs().subtract(months, "months")
      const nudgeTakes = user.nudge_takes.filter(
        (take) =>
          take.completed_at && dayjs(take.completed_at).isAfter(sixMonthsAgo)
      )
      allNudgeTakes = [...allNudgeTakes, ...nudgeTakes]
    })
  })
  const nudgeTakesByType = new Map<string, NudgeTakeFragment[]>()
  allNudgeTakes.forEach((take) => {
    const name = take.nudge_schedule.nudge.technique
    const takes = nudgeTakesByType.get(name) ?? []
    takes.push(take)
    nudgeTakesByType.set(name, takes)
  })
  const nudgeTakes = Array.from(nudgeTakesByType.entries()).sort((a, b) =>
    a[1].length < b[1].length ? 1 : a[1].length > b[1].length ? -1 : 0
  )
  const newTechniqueProfileData: TechniqueProfileData = {
    topTenTechniques: Array.from(nudgeTakesByType.keys())
      .map((label) => {
        const takes = nudgeTakesByType.get(label) ?? []
        return {
          label,
          domain: takes[0].nudge_schedule.nudge.domain as NudgeDomain,
          percent: takes.length / allNudgeTakes.length,
        }
      })
      .sort((a, b) =>
        a.percent < b.percent ? 1 : a.percent > b.percent ? -1 : 0
      ),
    mostUsed: nudgeTakes.slice(0, 3).map((take) => ({
      label: take[0],
      domain: take[1][0].nudge_schedule.nudge.domain as NudgeDomain,
      count: take[1].length,
    })),
    leastUsed: nudgeTakes
      .slice(-3)
      .map((take) => ({
        label: take[0],
        domain: take[1][0].nudge_schedule.nudge.domain as NudgeDomain,
        count: take[1].length,
      }))
      .sort((a, b) => (a.count < b.count ? -1 : a.count > b.count ? 1 : 0)),
  }
  return newTechniqueProfileData
}

const staffTableDataState = atom<StaffTableData[] | undefined>({
  key: "staffTableDataState",
  default: undefined,
})

const staffOverviewState = atom<StaffOverviewData | undefined>({
  key: "staffOverviewState",
  default: undefined,
})

const techniqueProfileState = atom<TechniqueProfileData | undefined>({
  key: "techniqueProfileState",
  default: undefined,
})

const clientProgressState = atom<ClientProgressData | undefined>({
  key: "clientProgressState",
  default: undefined,
})

// any type for now, as this is used in the SupervisorTable
const supervisorTableDataState = atom<any | undefined>({
  key: "supervisorTableDataState",
  default: undefined,
})

export const useOrganization = () => {
  const { user } = useUser()
  const queryClient = useQueryClient()
  const graphQLClient = useGraphQLClient()
  const [staffTableData, setStaffTableData] =
    useRecoilState(staffTableDataState)
  const [staffOverviewData, setStaffOverviewData] =
    useRecoilState(staffOverviewState)
  const [techniqueProfileData, setTechniqueProfileData] = useRecoilState(
    techniqueProfileState
  )
  const [clientProgressData, setClientProgressData] =
    useRecoilState(clientProgressState)
  const [supervisorTableData, setSupervisorTableData] = useRecoilState(
    supervisorTableDataState
  )
  const onSuccess = () =>
    queryClient.invalidateQueries({ queryKey: [Query.GET_PRACTITIONER] })
  const { mutate: updateOrgName } = useUpdateOrganizationNameMutation(
    graphQLClient,
    { onSuccess }
  )
  const { mutate: updateOrgLocation } = useUpdateOrganizationLocationMutation(
    graphQLClient,
    { onSuccess }
  )

  const {
    error,
    data: dashboardData,
    refetch: refetchMyOrg,
  } = useMyOrganizationQuery(graphQLClient, {
    orgId: user?.organizations?.[0].id ?? -1,
    date: dayjs().subtract(14, "day").startOf("day").toISOString(),
  })

  const { data: staffData, refetch: refetchStaffData } =
    useGetOrgPractitionerCountsQuery(graphQLClient, {
      orgId: user?.organizations?.[0].id ?? -1,
    })

  // Get supervisors and their supervisees
  const { data: supervisorData, refetch: refetchSupervisors } =
    useGetSupervisorsByOrgIdQuery(graphQLClient, {
      org_id: user?.organizations?.[0].id ?? -1,
    })

  useEffect(() => {
    if (!staffData) {
      return
    }

    setStaffTableData([
      ...staffData?.pending.map((member) => {
        return {
          id: {
            sortData: "",
            data: member.id,
          },
          firstName: {
            sortData: member.first_name,
            data: member.first_name,
          },
          lastName: {
            sortData: member.last_name,
            data: member.last_name,
          },
          viraClients: {
            sortData: Pending,
            data: Pending,
          },
          nudges: {
            sortData: "",
            data: "",
          },
          questionnaires: {
            sortData: "",
            data: "",
          },
          casemix: {
            data: "",
            sortData: "",
          },
          more: {
            sortData: "",
            data: {
              email: member.email,
              role: member.role,
              pending: true,
            },
          },
        }
      }),
      ...staffData?.active.map(({ practitioner }) => {
        return {
          id: {
            sortData: practitioner.id,
            data: practitioner.id,
          },
          firstName: {
            sortData: practitioner.first_name,
            data: practitioner.first_name,
          },
          lastName: {
            sortData: practitioner.last_name,
            data: practitioner.last_name,
          },
          viraClients: {
            sortData: practitioner.client_count.aggregate?.count ?? 0,
            data: practitioner.client_count.aggregate?.count ?? 0,
          },
          nudges: {
            sortData: practitioner.nudge_count.aggregate?.count ?? 0,
            data: practitioner.nudge_count.aggregate?.count ?? 0,
          },
          questionnaires: {
            sortData: practitioner.questionnaire_count.aggregate?.count ?? 0,
            data: practitioner.questionnaire_count.aggregate?.count ?? 0,
          },
          // left in as this allows space for column in staff table
          casemix: {
            data: 0,
            sortData: 0,
          },
          more: {
            sortData: "",
            data: {
              email: practitioner.email,
              role:
                practitioner.account?.user.organization_admins &&
                practitioner.account?.user.organization_admins.length > 0
                  ? "admin"
                  : practitioner.account?.user.users_role_capabilities?.[0]
                      ?.role ?? "practitioner",
            },
          },
        }
      }),
    ])
  }, [staffData, setStaffTableData])

  useEffect(() => {
    if (!dashboardData) {
      return
    }
    setStaffOverviewData({
      viraClients: dashboardData?.clientCount[0]?.count,
      nudges: dashboardData?.nudgeCount[0]?.count,
      questionnaires: dashboardData?.questionnaireCount[0]?.count,
    })

    if (dashboardData) {
      setTechniqueProfileData({
        topTenTechniques: dashboardData.topTenActiveNudges.map((technique) => {
          return {
            active: technique.active as boolean,
            label: technique.technique as string,
            domain: technique.domain as NudgeDomain,
            percent: technique.count / dashboardData.nudgeCount[0].count,
          }
        }),
        all: dashboardData.allActiveNudges.map((technique) => {
          return {
            active: technique.active as boolean,
            label: technique.technique as string,
            domain: technique.domain as NudgeDomain,
            percent: technique.count / dashboardData.nudgeCount[0].count,
          }
        }),
        mostUsed: dashboardData.mostActiveNudges.map((technique) => {
          return {
            active: technique.active as boolean,
            label: technique.technique as string,
            domain: technique.domain as NudgeDomain,
            count: technique.count,
          }
        }),
        leastUsed: dashboardData.leastActiveNudges.map((technique) => {
          return {
            active: technique.active as boolean,
            label: technique.technique as string,
            domain: technique.domain as NudgeDomain,
            count: technique.count,
          }
        }),
      })
    }

    const newClientProgressData = [[{ x: "N/A", y: 0 }]] //getClientProgress(organization)
    setClientProgressData(newClientProgressData)
  }, [
    dashboardData,
    setClientProgressData,
    setStaffOverviewData,
    setStaffTableData,
    setTechniqueProfileData,
  ])

  useEffect(() => {
    if (!supervisorData) {
      return
    }

    if (user.isOrgAdmin) {
      // If the user is an org admin, we want to show all supervisors
      const entries = supervisorData.organization_supervisors

      const supervisorMap = new Map<string, any>()

      for (let entry of entries) {
        //const supervisee = supervisor.supervisee
        const supervisorId = entry.supervisor?.id
        const supervisees = []
        for (let history of entry.organization_supervisors_organization_supervisees) {
          supervisees.push({
            id: history.supervisees?.id,
            firstName: history.supervisees?.first_name,
            lastName: history.supervisees?.last_name,
            displayName: history.supervisees?.display_name,
          })
        }
        supervisorMap.set(supervisorId, {
          supervisor: entry.supervisor,
          supervisee: supervisees,
        })
      }
      const supervisorArray = Array.from(supervisorMap.values())
      setSupervisorTableData(supervisorArray)
    }
  }, [supervisorData, setSupervisorTableData])

  if (error) {
    console.log(error)
  }

  return {
    staffTableData,
    staffOverviewData,
    techniqueProfileData,
    clientProgressData,
    refetchMyOrg,
    updateName(name: string) {
      updateOrgName({
        name,
        id: user?.organizations?.[0].id ?? -1,
      })
    },
    updateLocation(location: string) {
      updateOrgLocation({
        location,
        id: user?.organizations?.[0].id ?? -1,
      })
    },
    supervisorTableData,
    refetchSupervisors,
  }
}
